import { useClient } from '@cortex/react-query';
import type { Workflow } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type CSSProperties } from 'react';

import { EmptyState } from '../../../components/common/EmptyState';
import { Spinner } from '../../../components/common/Spinner';
import { WorkflowCanvas } from '../../../components/workflows/WorkflowCanvas';
import {
  useWorkflowEditor,
  WorkflowEditorProvider,
} from '../../../components/workflows/WorkflowEditorContext';
import { WorkflowTopBar } from '../../../components/workflows/WorkflowTopBar';
import { WorkflowVersionViewBanner } from '../../../components/workflows/WorkflowVersionViewBanner';
import {
  RightPanel,
  resolvePanelMode,
  type RightPanelMode,
} from '../../../components/workflows/right-panel';
import { ResizableRightPanel } from '../../../components/workflows/right-panel/ResizableRightPanel';
import { routes } from '../../../lib/constants';

// Search-param shape for `/workflows/:id`. Inlined into `validateSearch`
// rather than declared as a named interface because TanStack Router's
// generated route tree complains when the type name is private — keeping
// it structural makes the type nameable inline and dodges the export.
export const Route = createFileRoute('/workflows/$workflowId/')({
  component: WorkflowDetail,
  validateSearch: (
    raw: Record<string, unknown>,
  ): {
    panel: RightPanelMode;
    nodeKey?: string;
    edgeId?: string;
    runId?: string;
    version?: number;
  } => {
    const panel = resolvePanelMode(typeof raw.panel === 'string' ? raw.panel : undefined);
    const out: {
      panel: RightPanelMode;
      nodeKey?: string;
      edgeId?: string;
      runId?: string;
      version?: number;
    } = {
      panel,
    };
    // `version` pins the canvas to a historical (non-current) version,
    // rendered read-only. Coerce from string (URL) → number; drop anything
    // non-positive or non-finite so a junk param falls back to current.
    const rawVersion = typeof raw.version === 'string' ? Number(raw.version) : raw.version;
    if (typeof rawVersion === 'number' && Number.isInteger(rawVersion) && rawVersion > 0) {
      out.version = rawVersion;
    }
    // `nodeKey` is meaningful for both the Inspector panel and the Schemas
    // panel (the latter shows per-node inputMapping / outputSchema /
    // outputMapping under a "Node Schema" group when one is selected).
    if (
      (panel === 'node' || panel === 'schemas') &&
      typeof raw.nodeKey === 'string' &&
      raw.nodeKey.length > 0
    ) {
      out.nodeKey = raw.nodeKey;
    }
    // `edgeId` only meaningful on the Edge inspector panel. (DEV-16183.)
    if (panel === 'edge' && typeof raw.edgeId === 'string' && raw.edgeId.length > 0) {
      out.edgeId = raw.edgeId;
    }
    if (panel === 'runs' && typeof raw.runId === 'string' && raw.runId.length > 0) {
      out.runId = raw.runId;
    }
    return out;
  },
});

// Full-bleed: top bar in normal flow, then canvas + resizable right panel
// fill the rest of the viewport. The route's <main> runs in non-constrained
// mode via __root.tsx's `isFullBleed` discriminator.
const splitRow: CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
};

const canvasArea: CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

function WorkflowDetail() {
  const { workflowId } = Route.useParams();
  const search = Route.useSearch();
  const client = useClient();

  const wf = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}`).json<Workflow>(),
  });

  if (wf.isLoading) return <Spinner />;
  if (wf.error)
    return <div className="alert alert--danger">Failed to load: {(wf.error as Error).message}</div>;
  if (!wf.data) return <EmptyState title="Not found" description="Workflow does not exist." />;

  return (
    <WorkflowEditorProvider workflow={wf.data} viewVersionNumber={search.version ?? null}>
      <WorkflowDetailShell />
    </WorkflowEditorProvider>
  );
}

function WorkflowDetailShell() {
  const search = Route.useSearch();
  const nav = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const client = useClient();
  const editor = useWorkflowEditor();
  const w = editor.workflow;

  // Auto-pin the freshly-started run into the URL exactly once per
  // `activeRunId`. After that one auto-pin, if the user navigates away
  // (e.g. closes the run tail), we respect their choice — the previous
  // version of this effect re-pinned aggressively, making it impossible
  // to leave a run detail view.
  const lastAutoPinnedRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editor.activeRunId) return;
    if (lastAutoPinnedRunRef.current === editor.activeRunId) return;
    if (search.panel === 'runs' && search.runId === editor.activeRunId) {
      lastAutoPinnedRunRef.current = editor.activeRunId;
      return;
    }
    lastAutoPinnedRunRef.current = editor.activeRunId;
    fork(
      nav({
        replace: true,
        search: {
          panel: 'runs' as const,
          runId: editor.activeRunId,
          ...(search.version != null ? { version: search.version } : {}),
        },
      }),
    );
  }, [editor.activeRunId, search.panel, search.runId, search.version, nav]);

  const setPanel = (
    next: RightPanelMode,
    extras?: { nodeKey?: string; edgeId?: string; runId?: string },
  ) => {
    const out: {
      panel: RightPanelMode;
      nodeKey?: string;
      edgeId?: string;
      runId?: string;
      version?: number;
    } = {
      panel: next,
    };
    if (next === 'node' || next === 'schemas') {
      const key = extras?.nodeKey ?? search.nodeKey;
      if (key) out.nodeKey = key;
    } else if (next === 'edge') {
      const id = extras?.edgeId ?? search.edgeId;
      if (id) out.edgeId = id;
    } else if (next === 'runs') {
      const id = extras?.runId;
      if (id) out.runId = id;
    }
    // Keep the pinned historical version across panel switches — switching
    // tabs shouldn't kick the user back to the current version.
    if (search.version != null) out.version = search.version;
    fork(nav({ search: out }));
  };

  const duplicate = useMutation({
    mutationFn: () => client.http.post(`v1/workflows/${w.id}/duplicate`).json<Workflow>(),
    onSuccess: async (copied) => {
      try {
        await qc.invalidateQueries({ queryKey: ['workflows'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.workflowDetail(copied.id), search: { panel: 'overview' } });
      } catch (err) {
        console.error(err);
      }
    },
  });

  return (
    <div style={splitRow}>
      <div style={canvasArea}>
        <WorkflowTopBar
          // After the "+ Add node" dropdown drops a new node, flip the
          // right panel to its inspector — same end state the old
          // Palette tab landed on.
          onSelectNode={(nodeKey) => setPanel('node', { nodeKey })}
          onDuplicate={() => duplicate.mutate()}
          isDuplicating={duplicate.isPending}
          onScheduled={() => setPanel('schedules')}
        />
        {duplicate.error ? (
          <div
            className="alert alert--danger"
            style={{ margin: 'var(--sp-2) var(--sp-3)', fontSize: 12 }}
          >
            {(duplicate.error as Error).message}
          </div>
        ) : null}
        <WorkflowVersionViewBanner
          onBackToCurrent={() => fork(nav({ search: (prev) => ({ ...prev, version: undefined }) }))}
        />
        <WorkflowCanvas
          selectedNodeKey={search.panel === 'node' ? (search.nodeKey ?? null) : null}
          selectedEdgeId={search.panel === 'edge' ? (search.edgeId ?? null) : null}
          onSelectNode={(nodeKey) => {
            if (nodeKey === null) {
              // Clicking empty canvas: collapse the inspector if it was
              // the visible mode, OR clear the selection when on the
              // Schemas tab (which swaps Workflow ⇄ Node schema based on
              // selection — without this you can't get back to the
              // workflow view without switching tabs). Other panels are
              // unaffected.
              if (search.panel === 'node') {
                setPanel('overview');
              } else if (search.panel === 'edge') {
                setPanel('overview');
              } else if (search.panel === 'schemas') {
                // setPanel falls back to `search.nodeKey` when no extras
                // arg is provided, so go through nav directly to drop it.
                // Preserve the `version` pin — clearing the node selection
                // must NOT kick the user back to the current version.
                fork(
                  nav({
                    search: {
                      panel: 'schemas' as const,
                      ...(search.version != null ? { version: search.version } : {}),
                    },
                  }),
                );
              }
            } else {
              setPanel(search.panel === 'schemas' ? 'schemas' : 'node', { nodeKey });
            }
          }}
          onSelectEdge={(edgeId) => {
            // Clicking an edge always flips to the Edge inspector — the
            // panel reads the edge's `boardColumnId` for the dropdown
            // and surfaces the from/to + condition. (DEV-16183.)
            if (edgeId) {
              setPanel('edge', { edgeId });
            }
          }}
        />
      </div>
      <ResizableRightPanel>
        <RightPanel
          panel={search.panel}
          nodeKey={search.nodeKey ?? null}
          edgeId={search.edgeId ?? null}
          runId={search.runId ?? null}
          onChangePanel={setPanel}
        />
      </ResizableRightPanel>
    </div>
  );
}
